import React, { ReactNode, useCallback, useContext, useRef, useState } from 'react';

type FormWithValidationContextType<T> = {
  formData: T;
  addFormData: (data: T) => void;
  updateFormData: (data: T) => void;
  addFormValidation: (key: string, isValid: boolean) => void;
  removeFormValidation: (key: string) => void;
  resetDirty: () => void;
  isValid: boolean;
  isDirty: boolean;
};

export type EditedValue = Record<string, any>;
export type ValidatedValue = Record<string, boolean>;

export const FormWithValidationContext = React.createContext<FormWithValidationContextType<any>>(
  {} as FormWithValidationContextType<any>
);

export function useFormWithValidation<T>() {
  return useContext<FormWithValidationContextType<T>>(
    FormWithValidationContext as unknown as React.Context<FormWithValidationContextType<T>>
  );
}

export type Props = {
  children: ReactNode;
};

function FormWithValidationContextProvider<T>({ children }: Props) {
  const [formData, setFormData] = useState<T | null>(null);
  const validatedValues = useRef<ValidatedValue>({});
  const [isDirty, setIsDirty] = useState(false);
  const [isValid, setIsValid] = useState(true);

  const addFormValidation = useCallback(
    (key: string, isValid: boolean) => {
      validatedValues.current[key] = isValid;
      setIsValid(Object.values(validatedValues.current).every((valid) => valid));
    },
    [validatedValues]
  );

  const removeFormValidation = useCallback(
    (key: string) => {
      delete validatedValues.current[key];
      setIsValid(Object.values(validatedValues.current).every((valid) => valid));
    },
    [validatedValues]
  );

  const addFormData = useCallback((data: T) => {
    setFormData(data);
  }, []);

  const updateFormData = useCallback((data: T) => {
    setFormData(data);
    setIsDirty(true);
  }, []);

  const resetDirty = useCallback(() => {
    setIsDirty(false);
  }, []);

  return (
    <FormWithValidationContext.Provider
      value={{
        addFormData,
        updateFormData,
        formData,
        addFormValidation,
        removeFormValidation,
        resetDirty,
        isValid,
        isDirty,
      }}
    >
      {children}
    </FormWithValidationContext.Provider>
  );
}

export default FormWithValidationContextProvider;
